import * as React from "react";
import { actionCreators } from "../login";
import { bindActionCreators, Dispatch } from "redux";
import { connect } from "react-redux";
// @ts-ignore
import { State } from "../store/reducer";

const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes
const THROTTLE_MS = 30000;
const EVENTS = ["mousemove", "keydown", "scroll", "touchstart", "click"];

interface StateToProps {
  isLoggedIn: boolean;
}

interface DispatchToProps {
  logout: typeof actionCreators.logout;
}

interface SessionGuardProps extends StateToProps, DispatchToProps {}

const SessionGuard = (props: SessionGuardProps) => {
  const timerRef = React.useRef<ReturnType<typeof setTimeout>>();
  const throttledRef = React.useRef(false);
  const throttleTimerRef = React.useRef<ReturnType<typeof setTimeout>>();
  const lastActivityRef = React.useRef(Date.now());
  const logoutRef = React.useRef(props.logout);
  logoutRef.current = props.logout;

  React.useEffect(() => {
    if (!props.isLoggedIn) {
      return;
    }

    // Reset throttle state for fresh session
    throttledRef.current = false;
    if (throttleTimerRef.current) {
      clearTimeout(throttleTimerRef.current);
    }

    // Single source of truth: schedule logout based on lastActivityRef
    const scheduleLogout = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = INACTIVITY_TIMEOUT - elapsed;
      if (remaining <= 0) {
        logoutRef.current();
        return;
      }
      timerRef.current = setTimeout(() => logoutRef.current(), remaining);
    };

    const handleActivity = () => {
      if (throttledRef.current) {
        return;
      }
      throttledRef.current = true;
      throttleTimerRef.current = setTimeout(() => {
        throttledRef.current = false;
      }, THROTTLE_MS);
      lastActivityRef.current = Date.now();
      scheduleLogout();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        return;
      }
      // Re-evaluate based on last activity; timer may have been throttled
      scheduleLogout();
    };

    EVENTS.forEach((e) =>
      window.addEventListener(e, handleActivity, { passive: true }),
    );
    document.addEventListener("visibilitychange", handleVisibilityChange);
    lastActivityRef.current = Date.now();
    scheduleLogout();

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current);
      }
      EVENTS.forEach((e) => window.removeEventListener(e, handleActivity));
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [props.isLoggedIn]);

  return <React.Fragment />;
};

function mapStateToProps({ login: { isLoggedIn } }: State): StateToProps {
  return {
    isLoggedIn,
  };
}

function mapDispatchToProps(dispatch: Dispatch): DispatchToProps {
  return bindActionCreators(
    {
      logout: actionCreators.logout,
    },
    dispatch,
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(SessionGuard);
